import { getSafePostLoginRedirect } from '@shared/safe-post-login-redirect';
import { CWUser } from 'cw-isdk/core';
import React, { useEffect, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import Container from 'typedi';
import { AnonymousShell } from '../../anonymous-shell/anonymous-shell.component';
import { useCurrentUser } from '../../global-hooks/use-current-user.hook';
import { IExtendedOauth2LoginLink } from '../../tid-oauth-auth.service';
import { Urls } from '../../urls';
import { CookieBanner } from './cookie-banner.component';
import { ImpersonationLoginInfo, ImpersonationLoginModal } from './impersonation-log-in-modal.component';
import { LogInActionService } from './log-in-action.service';
import { LogInForm } from './log-in-form.component';
import { LogInData } from './log-in.data';
import { useHideDefaultLogin } from './use-hide-default-login.hook';
import { useOauth2Clients } from './use-oauth2-clients.hook';

export interface LoginWindow {
    CwLogInPage: {
        defaultDoNotTrack: boolean;
        impersonationLoginInfo: ImpersonationLoginInfo | null;
        loginUrl: string;
        silentRedirectUrl: string;
    };
}
const loginWindow = window as unknown as LoginWindow;

export function LogIn(): React.ReactElement {

    const { loading: loadingUser, user } = useCurrentUser();
    const { loading: loadingOAuthClients, data: oAuthData } = useOauth2Clients();
    const { loading: loadingHideDefaultLogin, hideDefaultLogin } = useHideDefaultLogin();

    const isLoading = loadingUser || loadingOAuthClients || loadingHideDefaultLogin;
    let body: React.ReactElement;
    if (isLoading) {
        body = <Spinner animation='border' variant='primary' />;
    } else {
        body = (
            <Loaded
                hideDefaultLogin={hideDefaultLogin}
                oauth2LoginLinks={oAuthData?.map(itm => {
                    (itm as IExtendedOauth2LoginLink).PostLogoutRedirectUri = loginWindow.CwLogInPage?.loginUrl;
                    (itm as IExtendedOauth2LoginLink).SilentRedirectUri = loginWindow.CwLogInPage?.silentRedirectUrl;
                    return itm as IExtendedOauth2LoginLink;
                }) ?? []}
                user={user} />
        );
    }

    return (
        <AnonymousShell
            title='Login'
            render={() =>
                <div>{body}</div>
            } />
    );
}

function Loaded(props: LoadedProps): React.ReactElement {

    const [mfaError, setMfaError] = useState<string | undefined>();
    const [mfaAttemptsRemaining, setMfaAttemptsRemaining] = useState<number>(3);
    const [isImpersonationLoginPending, setIsImpersonationLoginPending] = useState(() => loginWindow.CwLogInPage.impersonationLoginInfo != null);

    useEffect(() => {
        if (!isImpersonationLoginPending && props.user != null) {
            loggedIn();
        }
    }, [isImpersonationLoginPending, props.user]);

    function handleMfaLogin(username: string, mfaToken: string, mfaCode: string, rememberMe: boolean): void {
        const loginData = Container.get(LogInData);
        loginData.mfaVerify(username, mfaToken, mfaCode, rememberMe).subscribe({
            next: (response) => {
                if (response.LoggedIn && response.User) {
                    loggedIn();
                } else if (response.Error) {
                    const attemptsLeft = mfaAttemptsRemaining - 1;
                    setMfaAttemptsRemaining(attemptsLeft);
                    if (attemptsLeft > 0) {
                        setMfaError(`Invalid verification code. ${attemptsLeft} attempt${attemptsLeft !== 1 ? 's' : ''} remaining.`);
                    } else {
                        setMfaError('Maximum attempts exceeded. Please start over.');
                    }
                } else {
                    setMfaError('Unknown error occurred during verification.');
                }
            },
            error: (err) => {
                console.error('MFA verification error:', err);
                setMfaError('Error verifying code. Please try again.');
            }
        });
    }

    function resetMfa(): void {
        setMfaError(undefined);
        setMfaAttemptsRemaining(3);
    }

    function handleResendMfaCode(username: string, mfaToken: string, onTokenUpdate: (newToken: string) => void): void {
        const loginData = Container.get(LogInData);
        loginData.resendMfaCode(username, mfaToken).subscribe({
            next: (response) => {
                if (response.Success && response.MfaToken) {
                    setMfaError(undefined);
                    onTokenUpdate(response.MfaToken);
                } else {
                    setMfaError('Failed to resend code. Please try again.');
                }
            },
            error: (err) => {
                console.error('Resend MFA code error:', err);
                setMfaError('Error resending code. Please try again.');
            }
        });
    }

    const impersonationModal = isImpersonationLoginPending
        ? <ImpersonationLoginModal
            impersonationLoginInfo={loginWindow.CwLogInPage.impersonationLoginInfo}
            onCancel={() => setIsImpersonationLoginPending(false)}
            onLoggedIn={() => loggedIn()}
            user={props.user} />
        : null;

    return (
        <>
            {impersonationModal}
            <LogInForm
                loggedIn={() => loggedIn()}
                mfaLogin={handleMfaLogin}
                mfaError={mfaError}
                mfaAttemptsRemaining={mfaAttemptsRemaining}
                resetMfa={resetMfa}
                resendMfaCode={handleResendMfaCode}
                oauth2={props.oauth2LoginLinks}
                hideDefaultLogin={props.hideDefaultLogin} />
            <CookieBanner />
        </>
    );
}

function loggedIn(): void {
    const navToUrl = getSafePostLoginRedirect(getNavigateTo()) ?? '';

    if (navToUrl.length > 0) {
        window.location.replace(navToUrl);
    } else {
        const value = Container.get(LogInActionService).getLogInAction();
        if (value === 'selectPlugin') {
            window.location.href = Urls.makeAppSelectionUrl().toString();
        } else {
            // load last app is the default action.
            window.location.href = Urls.makeSwitchAppUrl({ lastApp: true }).toString();
        }
    }
}

function getNavigateTo(): string {
    const url = new URL(window.location.href);
    return url.searchParams.get('to') ?? '';
}

interface LoadedProps {
    hideDefaultLogin: boolean;
    oauth2LoginLinks: IExtendedOauth2LoginLink[];
    user: CWUser | undefined;
}
